import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import GLASSES from "@/data/glass.json";

export default function DisposableGlassCollection() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12">
        <Link
          to="/products"
          data-testid="back-to-products"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
        >
          <ArrowLeft size={14} /> All products
        </Link>

        <div className="mt-8 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline">Beverage · Disposable Glass</p>
            <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              Disposable <span className="italic text-[#C27A62]">glass</span> collection.
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-[#5C665C] leading-relaxed">
              {GLASSES.length} designs of single-use paper cups — festive,
              floral, party and premium prints for retail, catering and
              hospitality.
            </p>
            <ul
              data-testid="glass-specs"
              className="mt-8 grid grid-cols-2 gap-4 max-w-md"
            >
              {[
                { k: "Volume", v: "350 ml" },
                { k: "Lining", v: "PE / PLA" },
                { k: "Print", v: "Custom · Foil" },
                { k: "MOQ", v: "On request" },
              ].map((s) => (
                <li key={s.k} className="border-t border-[#D1C9B8] pt-3">
                  <p className="overline">{s.k}</p>
                  <p className="font-serif text-xl mt-1 text-[#1C2C22]">{s.v}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section
        data-testid="glass-grid"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-16"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {GLASSES.map((g) => (
            <button
              key={g.code}
              data-testid={`glass-${g.code}`}
              onClick={() => setLightbox(g)}
              className="group text-left"
            >
              <div className="aspect-[4/5] bg-[#EBE5D9] border border-[#D1C9B8] overflow-hidden relative">
                <img
                  src={g.image}
                  alt={`${g.theme} disposable glass`}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 md:p-6 transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-[#F4F1EB] px-2 py-1 border border-[#D1C9B8]">
                  {g.theme}
                </span>
              </div>
              <div className="pt-4 flex items-baseline justify-between gap-3">
                <span className="font-serif text-xl tracking-tight text-[#1C2C22]">
                  {g.code}
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C665C]">
                  350 ml
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#1C2C22] text-[#F4F1EB] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="overline text-[#8A9A86]">Custom prints & bulk pricing</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">
              Order disposable glasses in bulk.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="glass-cta-contact"
            className="inline-flex items-center gap-3 border border-[#F4F1EB]/60 px-7 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#F4F1EB] hover:text-[#1C2C22]"
          >
            Request pricing & MOQ →
          </Link>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="glass-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-[#1C2C22]/85 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
        >
          <div
            className="max-w-2xl w-full bg-[#F4F1EB] border border-[#D1C9B8]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[4/5] bg-[#EBE5D9]">
              <img
                src={lightbox.image}
                alt={lightbox.code}
                className="w-full h-full object-contain p-6 md:p-10"
              />
            </div>
            <div className="p-6 md:p-8 flex items-center justify-between gap-6">
              <div>
                <p className="overline">{lightbox.theme}</p>
                <p className="font-serif text-3xl mt-2 tracking-tight">
                  {lightbox.code}
                </p>
                <p className="text-sm text-[#5C665C] mt-1">
                  350 ml · PE / PLA lining · Custom print
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="glass-lightbox-cta"
                  className="bg-[#1C2C22] text-[#F4F1EB] px-5 py-3 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62]"
                >
                  Enquire →
                </Link>
                <button
                  data-testid="glass-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
